import React, { cloneElement, useEffect, useState } from 'react'
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from 'react-i18next';
import { Save, X } from "lucide-react";
import { cn } from '@/lib/utils';
import { TextButton } from '@/components/common/text-button';
import { AddUserIcon } from '@/components/agent-dashboard/assets';
import { CreateForm, CreateFormProps } from './create-form';
import { agentPermissionHooks } from '@/manager';
import { PermissionUserData } from '@/services/agent-v2-list-agent-user';
import { PERMISSION_MAP } from '@/manager/manager/agent-dashboard-permission-manager';
import { AddAdminModalIcon } from './assets';

interface CreateAdminModalProps {
  type?: 'create' | 'update';
  details?: PermissionUserData;
  trigger?: React.ReactElement;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSuccess?: () => void;
}

/** 创建管理员弹窗 */
export const CreateAdminModal: React.FC<CreateAdminModalProps> = ({
  type = 'create',
  details,
  trigger,
  open: externalOpen,
  onOpenChange,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);

  const {
    loading: createLoading,
    createFormData,
    userNameError,
    emailError,
    roleTypeError,
    customPermission,
    opsFeatureConfigData,
    onCustomPermissionChange,
    changeValue,
    createAdmin,
    updateAdmin,
    setDetailToCreateFormData,
    clearCreateFormData,
  } = agentPermissionHooks.useCreateAndUpdateAdmin();

  // 判断是否使用 trigger 模式（trigger 优先）
  const isTriggerMode = !!trigger;

  // 根据模式决定使用哪个 open 状态
  const currentOpen = isTriggerMode ? internalOpen : externalOpen ?? false;

  // 处理 open 状态变化
  const handleOpenChange = (open: boolean) => {
    // 关闭时清空数据
    !open && clearCreateFormData();

    if (isTriggerMode) {
      setInternalOpen(open);
    } else {
      onOpenChange?.(open);
    }
  };

  const handleSave = async () => {
    try {
      let success = false;
      if (type === 'create') {
        success = await createAdmin();
      } else if (type === 'update') {
        success = await updateAdmin();
      }
      if (!success) return;
      handleClose();
      onSuccess?.();
    } catch (error) {
      return;
    }
  }

  // 关闭
  const handleClose = () => {
    handleOpenChange(false);
  };

  // trigger 点击处理
  const handleTriggerClick = () => {
    handleOpenChange(true);
  };

  // 表单值变化
  const handleValueChange = (data: Parameters<CreateFormProps['onValueChange']>[0]) => {
    if (data.name === 'roleType') {
      data.value = Number(data.value);
    }
    changeValue(data.name, data.value);
  }

  useEffect(() => {
    if (!details) return;
    setDetailToCreateFormData({
      userName: details.userName,
      email: details.email,
      roleType: details.roleType,
      id: details.id,
      userSubId: details.userSubId,
      // 转换为权限数组
      permissions: details.permissions.map(item => PERMISSION_MAP[item.split('_')[0]]),
    });
  }, [details])

  const dialogContent = (
    <Dialog.Portal>
      <Dialog.Overlay className={cn("fixed inset-0 bg-black/50 z-50")} />
      <Dialog.Content
        className={cn(
          "flex flex-col",
          "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
          "bg-white rounded-[1.25rem] shadow-lg",
          "w-[688px] min-h-[798px] flex-shrink-0",
          "z-50"
        )}>
        {/* 标题区域 */}
        <div className="flex max-h-[92px] justify-between items-center px-6 pt-6 pb-4 border-b">
          <Dialog.Title className="text-[rgba(24,29,39,1)] text-lg font-medium flex items-center">
            <div className='size-12 flex justify-center items-center rounded-lg mr-4 border border-[rgba(213,215,218,1)]'>
              <AddAdminModalIcon className='size-6' />
            </div>
            <span>{t('agent_dashboard.permission.create_modal.title')}</span>
          </Dialog.Title>
          <Dialog.Close asChild>
            <button
              className="w-10 h-10 flex justify-center items-center hover:bg-gray-100 border border-[rgba(208,213,221,1)] rounded-sm shadow-[0px_1px_2px_rgba(16,24,40,0.05)]"
              tabIndex={-1}
              onClick={handleClose}
            >
              <X className="w-4 h-4 text-[rgba(71,84,103,1)]" />
            </button>
          </Dialog.Close>
        </div>
        {/* 表单区域 */}
        <div className='flex-1 px-6 pt-5 mb-6'>
          <CreateForm
            {...createFormData}
            isEdit={type === 'update'}
            loading={createLoading}
            userNameError={userNameError}
            emailError={emailError}
            roleTypeError={roleTypeError}
            customPermission={customPermission}
            opsFeatureConfigData={opsFeatureConfigData}
            onCustomPermissionChange={onCustomPermissionChange}
            onValueChange={handleValueChange}
          />
        </div>
        {/* 操作按钮区域 */}
        <div className='w-full h-[93px] flex p-6 space-x-3 border-t border-[rgba(233,234,235,1)]'>
          <TextButton
            label={t('agent_dashboard.user_management.create_modal.cancel')}
            className='w-[314px] h-11 bg-white border border-[rgba(213,215,218,1)] hover:bg-gray-50 font-semibold text-base !text-[rgba(65,70,81,1)]'
            onClick={handleClose}
          />
          <TextButton
            label={t('agent_dashboard.user_management.create_modal.save')}
            className='w-[314px] h-11'
            loading={createLoading}
            onClick={handleSave}
            disabled={createLoading}
          />
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  )

  // trigger 模式
  if (isTriggerMode) {
    return (
      <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
        <Dialog.Trigger asChild>
          {cloneElement(trigger, {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              handleTriggerClick();
              // 保留原有的 onClick 事件
              if (trigger.props.onClick) {
                trigger.props.onClick(e);
              }
            },
          })}
        </Dialog.Trigger>
        {dialogContent}
      </Dialog.Root>
    );
  }

  // open 参数模式
  return (
    <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
      {dialogContent}
    </Dialog.Root>
  );
}
